import { ICellEditorAngularComp } from '@ag-grid-community/angular';
import { AfterViewInit, Component, ElementRef, HostListener, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Style } from '@app/core/services/core-component.service';
import { LayoutService } from '@app/core/services/layout.service';
import { Observable, debounceTime, switchMap, shareReplay, filter } from 'rxjs';
import { GridService } from '../../grid.service';
import { LookupCellEditorParams } from '../../grid.types';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { ListboxSelectEvent } from '@app/core/directives/listbox-popup/listbox-popup.directive';
import { ListboxStyle } from '@app/core/components/listbox/listbox.component';
import { PopupService } from '@app/core/services/popup.service';
import { ValueParserParams } from '@ag-grid-community/core';
import type { GridRefreshableCellEditor } from '../../helpers/grid-active-editor.helpers';
import { ModalService } from '@app/core/services/modal.service';

/**
 * Custom cell-editor component for lookup (search) cells.
 */
@Component({
    selector: 'tt-lookup-cell-editor',
    templateUrl: './lookup-cell-editor.component.html',
    styleUrls: ['./lookup-cell-editor.component.css'],
    standalone: false,
    encapsulation: ViewEncapsulation.None,
})
export class LookupCellEditorComponent implements ICellEditorAngularComp, AfterViewInit, GridRefreshableCellEditor {
    /**
     * Cell editor params passed from ag-grid.
     */
    public params?: LookupCellEditorParams;

    /**
     * Form-control for the search field.
     */
    public searchControl: FormControl<string | { [key: string]: string }> = new FormControl();

    /**
     * Whether the value in the cell has been edited since editing started.
     */
    public hasBeenEdited: boolean = false;

    /**
     * Whether change-event emitting should be suppressed, like when selecting an option.
     */
    private suppressValueChanges = false;

    /**
     * List of options to display from the search.
     */
    public filteredOptions?: Observable<{ [key: string]: string }[]>;

    /**
     * Whether forced search was triggered since the last change.
     */
    public _forcedSearch = false;

    /**
     * Element reference to the input element.
     */
    @ViewChild('inputRef')
    public inputRef?: ElementRef;

    /**
     * Reference to the trigger for the listbox displaying search options.
     */
    @ViewChild(MatAutocompleteTrigger)
    public autoComplete?: MatAutocompleteTrigger;

    /**
     * Whether the edited value in the cell should be cancelled or not.
     */
    private cancelEdit = false;

    /**
     * Forces search on key arrow down, closes listbox on key arrow up. Cancels editing when escape key is pressed.

     * @param event the keydown event that happened on the component.
     */
    //  @HostListener('keydown', ['$event'])
    public async keydown(event: KeyboardEvent) {
        if (event.key === 'Escape') {
            if (this.autoComplete?.panelOpen) {
                this.autoComplete.closePanel();
            } else {
                this.params?.api.stopEditing(true);
                this.params?.api.setFocusedCell(this.params.rowIndex, this.params.field);
            }
        } else if (typeof this.searchControl?.value === 'string' && this.searchControl?.value.trim() === '' && event.key === 'ArrowDown') {
            this.forceSearch();
        } else if (event.ctrlKey && event.key === 'ArrowUp') {
            this.autoComplete?.closePanel();
        } else if (event.key === 'Enter') {
            event.preventDefault();
            event.stopImmediatePropagation();
            event.stopPropagation();

            if (this.searchControl.value === '+') {
                this.params?.api.setGridOption('context', 'lookup.popup');
                const response = await this.gridService.getGotoParms({ loadDataTask: this.params?.options.dataTask?.loadData, field: `lookup:${this.params?.colDef.colId}`, row: this.params?.data, rememberId: this.params?.options.dataTask?.rememberId });

                if (!!response.item_state) {
                    this.params?.api.stopEditing();

                    if (response.item_parms?.includes('?ispopup')) {
                        await this.popup.openPopup(response.item_state + (!!response.item_parms ? '/' + response.item_parms : ''), { rememberId: !!this.params?.options.dataTask?.rememberId ? `${this.params?.options.dataTask?.rememberId}.popup:${this.params?.colDef.colId}` : undefined });

                        await new Promise<void>((resolve) => {
                            this.messagePromise = resolve;
                            window.addEventListener('message', this.setDataFromMessage);
                        });
                    } else {
                        const result = await this.modal.openStateDialog({ state: response.item_state, params: response.item_parms });

                        this.setDataThroughRelations(result);
                    }
                    // console.log(result);
                }
            }
        } else if (event.key === 'Tab') {
            // if (this.params?.autoSelectFirst !== false) {
            //     this.cancelEdit = true;
            // }

            if (this.params?.cancelOnTab === true) {
                this.cancelEdit = true;
            } else {
                this.params?.api.setFocusedCell(this.params.rowIndex, this.params.field);

                this.params?.onKeyDown(event as KeyboardEvent);
            }
        }

        setTimeout(() => {
            if (this.suppressValueChanges !== true) {
                this.params?.onKeyDown(event);
            }
        });
    }

    private messagePromise?: () => void;

    private setDataFromMessage = (event: MessageEvent<any>) => {
        if (Object.hasOwn(event.data, 'type') && event.data.type === 'ttData') {
            const data = event.data.data;

            this.setDataThroughRelations(data);

            window.removeEventListener('message', this.setDataFromMessage);
            this.messagePromise?.();
        }
    };

    /**
     * Styles for the elements in the template.
     */
    public style: Style = {
        input: {},
        listbox: {},
        option: {},
    };

    public listboxStyle: Partial<ListboxStyle> = {
        selectedOption: {
            background: 'var(--tt-primary-color)',
            color: 'var(--tt-primary-text-color)',
        },
    };

    public id = {
        wrapper: crypto.randomUUID(),
        input: crypto.randomUUID(),
    };

    constructor(
        layoutService: LayoutService,
        private gridService: GridService,
        private popup: PopupService,
        private modal: ModalService,
    ) {
        layoutService.layoutChanged.subscribe((info) => {
            if (info) {
                this.style['input'].fontSize = info.fontSizes.textSize;
                this.style['option'].fontSize = info.fontSizes.textSize;
                this.style['option'].minHeight = `calc(${info.fontSizes.textSize} + 5px)`;
                this.style['listbox'].margin = 'var(--ag-grid-size) 0';
                this.style['listbox'].minWidth = '25rem';
            }
        });
    }

    private markAsEdited(): void {
        this.hasBeenEdited = true;
        this.params?.onCellEditorValueChanged?.();
    }

    /**
     * Sets the same value again to trigger a change and thus a search to be called.
     */
    private forceSearch() {
        if (this._forcedSearch === true) return;
        this._forcedSearch = true;

        setTimeout(() => this.searchControl.setValue(this.searchControl.value), 250);
    }

    /**
     * Performs a search with the values passed in params and the given search string.
     *
     * @param search the filter-text to use for the search request.
     * @returns a list containing the result of the search request.
     */
    private async lookupSearch(search: string): Promise<{ [key: string]: string }[]> {
        if (search === '' && !this._forcedSearch) {
            this.autoComplete?.closePanel();
        } else if (this.params?.method && !isNaN(Number(this.params.method)) && this.params.field && this.params?.node.data) {
            return this.gridService.lookupSearch(Number(this.params.method), this.params.field, search, this.params?.node.data);
        }

        this._forcedSearch = false;

        return [];
    }

    /**
     * Sets the selected value and stops the cell editing.
     *
     * @param event the event transmitted for the selected option.
     */
    public onOptionSelected(event: ListboxSelectEvent<{ [key: string]: string }>) {
        this.markAsEdited();
        this.suppressValueChanges = true;
        this.searchControl.setValue(event.item, { onlySelf: true, emitEvent: false, emitModelToViewChange: false, emitViewToModelChange: false });

        setTimeout(() => {
            this.params?.api.stopEditing();
            this.params?.api.setFocusedCell(this.params.rowIndex, this.params.field);

            if (event.event.type === 'keydown') {
                this.params?.onKeyDown(event.event as KeyboardEvent);
            }
        });
    }

    public agInit(params: LookupCellEditorParams): void {
        this.params = params;
        this.style['input'].width = this.params.column.getActualWidth() + 'px';

        this.searchControl.setValue(this.params.value ?? '');
        this.searchControl.valueChanges.subscribe((value) => {
            this.markAsEdited();

            if (!value && !this._forcedSearch) {
                this.autoComplete?.closePanel();
            } else if (!!value) {
                this._forcedSearch = false;
            }
        });

        this.filteredOptions = this.searchControl.valueChanges.pipe(
            filter(() => !this.suppressValueChanges),
            debounceTime(250),
            switchMap((result) => (typeof result === 'string' ? this.lookupSearch(result) : this.lookupSearch(result[this.params!.colDef.colId!]))),
            shareReplay(1),
        ) as Observable<{ [key: string]: string }[]>;
    }

    public setValue(value: string, markAsEdited: boolean = true) {
        if (markAsEdited === true) {
            this.markAsEdited();
        }

        this.searchControl.setValue(value, { emitEvent: false });
    }

    public syncValueFromRow(value: unknown): void {
        this.setValue(value as string, false);
        setTimeout(() => {
            this.inputRef?.nativeElement.select();
        }, 1);
    }

    isCancelAfterEnd(): boolean {
        return this.cancelEdit || this.searchControl.value === '+';
    }

    private setDataThroughRelations(data: any) {
        let newData: any = {};

        if (!!data) {
            let newCurrentColumnValue;

            for (let { key: colKey, value: searchKey } of this.params!.relations) {
                try {
                    let value = data[searchKey] ?? '';

                    if (colKey === this.params?.colDef.colId) {
                        newCurrentColumnValue = value;
                    } else {
                        newData[colKey] = this.getParsedValue(colKey, value) || '';

                        if (!!this.params?.options.data?.dirtyRows && !this.params!.options.data!.dirtyRows[this.params!.node!.id!]?.cells?.includes(colKey!)) {
                            if (!this.params.options.data.dirtyRows[this.params!.node!.id!]) {
                                this.params.options.data.dirtyRows[this.params.node.id!] = { cells: [] };
                            }

                            this.params.options.data.dirtyRows[this.params!.node!.id!].cells.push(colKey!);
                        }
                    }
                } catch (error) {
                    console.error(error);
                }
            }

            const rowIndex = this.params?.options.data?.rowData.findIndex((row) => row._uuid === this.params?.node.id) ?? -1;

            if (!!this.params?.options.data?.rowData && rowIndex !== -1) {
                this.params.options.data.rowData[rowIndex] = { ...this.params.options.data.rowData[rowIndex], ...newData };

                // this sets data silently to ag grid.
                this.params?.node.setData(this.params.options.data.rowData[rowIndex]);
                // this triggers valuesetter and thus autosave events.
                this.params?.node.setDataValue(this.params!.colDef!.colId!, this.params.parseValue(!!newCurrentColumnValue ? newCurrentColumnValue : !!data[this.params!.colDef!.colId!] ? data[this.params.colDef.colId!] : data['item_name']));
            }
        }
    }

    private getParsedValue(colKey: string, value: any) {
        let column = this.params?.api.getColumn(colKey);
        let colDef = column?.getColDef();

        if (!!column && colDef?.valueParser instanceof Function) {
            return colDef.valueParser?.({ api: this.params!.api!, colDef: colDef!, column: column!, newValue: value!, oldValue: this.params!.data![colKey]!, context: undefined, data: this.params!.data!, columnApi: this.params!.columnApi! } as ValueParserParams<any, any>);
        } else {
            return value;
        }

        throw Error('undefined values');
    }

    public getValue() {
        if (typeof this.searchControl.value === 'object') {
            this.setDataThroughRelations(this.searchControl.value);

            return this.params?.node.data[this.params!.colDef.colId!];
        }

        if (!this.hasBeenEdited) {
            return this.params?.node.data[this.params!.colDef.colId!];
        }

        return this.params!.parseValue(this.searchControl.value);
    }

    // public ngAfterViewInit(): void {
    //     setTimeout(() => this.inputRef?.nativeElement.select(), 10);
    // }
    ngAfterViewInit() {
        window.setTimeout(() => {
            if (!!this.params && this.inputRef && (this.params.eventKey ?? '').length === 1) {
                this.inputRef.nativeElement.value = this.params.eventKey;
                this.inputRef?.nativeElement.focus();
            } else {
                this.inputRef?.nativeElement.focus();
                this.inputRef?.nativeElement.select();
            }
        });
    }
}
